import { createFileRoute, Link } from "@tanstack/react-router";
import { SafeImage } from "@/components/site/SafeImage";
import { ArticleDisclaimer } from "@/components/site/ArticleDisclaimer";
import { COUNTRIES } from "@/data/countries";
import logoAsset from "@/assets/lifewestway-logo.png.asset.json";
import { makeSeoDescription } from "@/lib/seo-description";
import { organizationSchema } from "@/lib/org-schema";

const SITE = "https://lifewestway.com";
const TITLE = "About Us | LifeWestWay";
const DESC =
  "LifeWestWay is an independent information platform helping people move to, work in, and build a life across the USA, UK, Australia, Italy, Portugal, and Canada — clear, practical guides without the noise.";
const URL_ = `${SITE}/about`;
const IMAGE = `${SITE}${logoAsset.url}`;

const COVER_AREAS = [
  {
    title: "Immigration & Visas",
    desc: "Visa pathways, timelines, eligibility requirements, and application checklists for every destination we cover.",
  },
  {
    title: "Jobs",
    desc: "Verified job search strategies, work rights, and remote work opportunities across the six countries.",
  },
  {
    title: "Tax",
    desc: "Country-specific tax obligations explained in plain language — residency rules, brackets, and filing duties.",
  },
  {
    title: "Finance & Banking",
    desc: "Opening accounts, building credit, deposit protection schemes, and money transfers for newcomers.",
  },
  {
    title: "Housing & Living Costs",
    desc: "Rent, deposits, insurance, and realistic budget breakdowns so you know what life actually costs.",
  },
  {
    title: "Residency & Citizenship",
    desc: "The path from a temporary visa to permanent status and, eventually, citizenship in each country.",
  },
  {
    title: "Government Benefits & Pensions",
    desc: "Welfare systems, public services, and retirement planning for residents and long-term settlers.",
  },
  {
    title: "Real Stories & Case Studies",
    desc: "Illustrative, realistic examples of how these processes play out financially for different applicant profiles.",
  },
];

const APPROACH = [
  {
    heading: "Accurate",
    body: "We reference official government sources — USCIS, UKVI, the Department of Home Affairs, AIMA, IRCC, and others — and update our content as rules change.",
  },
  {
    heading: "Practical",
    body: "We focus on what you actually need to do, not just abstract policy explanations. Step-by-step guides, document checklists, and realistic timelines.",
  },
  {
    heading: "Honest about its limits",
    body: "We are not a law firm, immigration consultancy, or financial advisory service. Where a decision genuinely depends on your personal circumstances, we say so and point you toward qualified professionals.",
  },
];

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: makeSeoDescription(DESC) },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: makeSeoDescription(DESC) },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL_ },
      { property: "og:image", content: IMAGE },
      { property: "og:site_name", content: "LifeWestWay" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: makeSeoDescription(DESC) },
      { name: "twitter:image", content: IMAGE },
    ],
    links: [{ rel: "canonical", href: "https://lifewestway.com/about" }],
  }),
  component: AboutPage,
});

function AboutPage() {
  const orgJsonLd = {
    "@context": "https://schema.org",
    ...organizationSchema({
      logo: IMAGE,
      image: IMAGE,
      description:
        "Independent information platform helping people move to, work in, and build a life across the USA, UK, Australia, Italy, Portugal, and Canada.",
      knowsAbout: [
        "Immigration",
        "Visas",
        "Work Permits",
        "Tax",
        "Banking",
        "Housing",
        "Residency",
        "Citizenship",
      ],
      areaServed: COUNTRIES.map((c) => c.name),
    }),
  };


  return (
    <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
        <Link title="Return to the LifeWestWay homepage" to="/" className="hover:text-accent">
          Home
        </Link>
        <span className="px-2">/</span>
        <span className="text-foreground">About Us</span>
      </nav>

      {/* H1 Hero */}
      <header className="mt-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">
          About LifeWestWay
        </p>
        <h1 className="mt-3 font-display text-4xl font-black tracking-tight text-foreground sm:text-5xl">
          Who We Are
        </h1>
        <p className="mt-5 max-w-3xl text-lg leading-relaxed text-foreground/75">
          LifeWestWay is an independent information platform dedicated to helping people navigate
          the journey of moving to, working in, and building a life across six major Western
          destinations: the United States, United Kingdom, Australia, Italy, Portugal, and Canada.
        </p>
      </header>

      {/* Why we started */}
      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-accent">Why We Started This Site</h2>
        <div className="mt-4 space-y-4 text-sm leading-7 text-foreground/80">
          <p>
            We started this website because we noticed a gap: most immigration and relocation
            information online is either scattered across dozens of government websites, buried in
            dense legal language, or presented by services trying to sell something. We wanted to
            build something different — a single, organized place where anyone considering a move
            abroad could find clear, practical, and genuinely useful information without the noise.
          </p>
        </div>
      </section>

      {/* What we cover */}
      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-accent">What We Cover</h2>
        <p className="mt-4 text-sm leading-7 text-foreground/80">
          Our content is organized around the real questions people ask when planning a move abroad.
          Each topic below is covered for all six destinations, with country-specific detail and
          cross-country comparisons where they help:
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {COVER_AREAS.map((area) => (
            <div
              key={area.title}
              className="rounded-xl border border-border bg-card/60 p-5 transition-colors hover:border-accent/40"
            >
              <h3 className="font-display text-base font-semibold text-foreground">
                {area.title}
              </h3>
              <p className="mt-2 text-sm leading-6 text-foreground/70">{area.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Countries we serve */}
      <section className="mt-12">
        <h3 className="font-display text-xl font-semibold text-accent/90">
          The six destinations we cover
        </h3>
        <div className="mt-4 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {COUNTRIES.map((c) => (
            <Link title={`Explore the ${c.name} immigration, jobs and tax guide`}
              key={c.slug}
              to="/$country"
              params={{ country: c.slug }}
              className="group flex flex-col items-center gap-2 rounded-xl border border-border bg-card/40 p-4 text-center transition-all hover:border-accent hover:shadow-md"
            >
              <SafeImage width={80} height={60}
                src={`https://flagcdn.com/w80/${c.code}.webp`}
                alt={`${c.name} flag`}
                loading="lazy"
                decoding="async"
                className="h-8 w-12 rounded object-cover ring-1 ring-border"
              />
              <span className="text-sm font-medium text-foreground group-hover:text-accent">
                {c.name}
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Our approach */}
      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-accent">Our Approach</h2>
        <p className="mt-4 text-sm leading-7 text-foreground/80">
          We believe good information should be three things at once. Here is the standard every
          guide on LifeWestWay is held to:
        </p>
        <div className="mt-6 space-y-4">
          {APPROACH.map((a) => (
            <div
              key={a.heading}
              className="rounded-xl border border-border bg-card/60 p-5"
            >
              <h3 className="font-display text-base font-semibold text-foreground">
                {a.heading}
              </h3>
              <p className="mt-2 text-sm leading-6 text-foreground/70">{a.body}</p>
            </div>
          ))}
        </div>
        <p className="mt-5 text-sm leading-7 text-foreground/80">
          For the full detail of how we research, source, review, and update every article —
          including which government bodies we cite for each country — see our{" "}
          <Link title="How LifeWestWay researches and fact-checks guides" to="/editorial-process" className="font-semibold text-accent hover:underline">
            Editorial Process
          </Link>{" "}
          page.
        </p>
      </section>

      {/* A note on our content */}
      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-accent">A Note on Our Content</h2>
        <div className="mt-4 space-y-4 text-sm leading-7 text-foreground/80">
          <p>
            Some of our articles include illustrative case studies and example scenarios designed to
            help readers understand how financial and immigration processes typically unfold in
            practice. These are clearly marked as composite, educational examples — not real,
            identifiable individuals — created to make complex processes easier to understand.
          </p>
          <p>
            Every image on this site is either licensed, public domain, or owned by us, and each one
            is credited on our{" "}
            <Link title="See image and media credits for LifeWestWay" to="/media-credits" className="font-semibold text-accent hover:underline">
              Media Credits
            </Link>{" "}
            page. We do not publish scraped or spun content, and we do not create pages that exist
            purely to rank — if a topic does not warrant a full, useful guide, it becomes a section
            inside an existing one instead of a thin standalone page.
          </p>
          <p>
            Depth is chosen over volume. A country hub only ships once it can answer the four
            questions that actually decide a move — which route lets you in, what work realistically
            pays after tax, when you become tax resident and what that costs, and what a normal
            month of rent, transport, healthcare and schooling looks like outside the tourist
            centres. That is why the site covers six destinations properly rather than thirty
            superficially, and why some obvious keyword topics simply do not exist here: if we
            cannot source a claim from a government body or a national statistics office, we would
            rather leave the gap visible than fill it with plausible-sounding filler.
          </p>
          <p>
            We write for the reader mid-decision, not the algorithm. That shapes small things —
            plain English instead of legislative phrasing, figures with the year attached, and an
            explicit note when a rule is unsettled or awaiting confirmation — and larger ones, such
            as refusing to declare any single country the &ldquo;best&rdquo; option. The right
            destination depends on your occupation, family, savings and appetite for risk, so our
            job is to lay the trade-offs side by side and let you weigh them, not to nudge you
            toward whichever route happens to be easiest to write about.
          </p>
          <p>
            Reader corrections are part of the process. People in the middle of an application
            often discover a changed fee or a closed route before it filters into official guidance
            summaries, and those messages have improved more than one guide on this site. When we
            get something wrong we amend the article, note the change and re-date it rather than
            quietly editing the page, because a dated correction is worth more to the next reader
            than an invisible one.
          </p>
        </div>
      </section>


      {/* Moved from the homepage: what the site is, who it's for, mission */}
      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-accent">
          A plain-English guide to building a life in the West
        </h2>
        <div className="mt-4 space-y-4 text-sm leading-7 text-foreground/80">
          <p>
            LifeWestWay is an independent editorial site about moving to, working in and settling in
            six Western countries: the United States, the United Kingdom, Canada, Australia, Italy
            and Portugal. We cover the four things that actually decide whether a move works — the
            visa that lets you in, the job that pays for it, the tax you will owe once you arrive,
            and the banking, housing and everyday costs nobody warns you about until you land.
          </p>
          <p>
            It is written for people making a real decision, not browsing: skilled workers weighing
            a sponsored offer, students comparing post-study work rights, remote workers checking
            whether a digital-nomad visa is worth the tax exposure, families costing out rent and
            childcare before they commit, and returning citizens untangling residency. If you are
            earlier in the process and simply wondering which country suits your profile, the
            country guides are designed to be read side by side.
          </p>
          <p>
            How to use the site: start with a{" "}
            <Link title="Open the USA country hub for visas, jobs and tax" to="/$country" params={{ country: "usa" }} className="font-semibold text-accent hover:underline">
              country hub
            </Link>{" "}
            if you already know where you want to go — each one opens with an overview of visa
            routes, the job market, tax basics and living costs before linking to the detailed
            guides. Start with a topic hub instead —{" "}
            <Link title="Compare immigration and visa routes across six countries" to="/immigration" className="font-semibold text-accent hover:underline">Immigration</Link>,{" "}
            <Link title="Compare job markets and salaries across six countries" to="/jobs" className="font-semibold text-accent hover:underline">Jobs</Link>,{" "}
            <Link title="Compare tax residency and filing rules by country" to="/tax" className="font-semibold text-accent hover:underline">Tax</Link> or{" "}
            <Link title="Compare banking and personal finance options abroad" to="/finance" className="font-semibold text-accent hover:underline">Finance</Link>{" "}
            — if you want to compare the same question across all six destinations. Every article
            shows when it was last updated, links to the official government source behind each
            rule, and ends with the same disclaimer: we explain the system, but we are not your
            lawyer, accountant or migration agent.
          </p>
          <p>
            What each country hub covers: the main work, study, family and investment visa routes
            with their 2026 fees and processing times; which occupations are in demand and what they
            realistically pay after tax; how tax residency is triggered and what happens to income
            you still earn back home; and the running cost of a normal month — rent in the big
            cities versus second-tier ones, health cover, transport, schooling and childcare. Where
            a country runs a regional or state-nominated stream, such as Australia's state
            nomination or Canada's Provincial Nominee Program, we explain how the points and
            eligibility differ from the federal route rather than treating them as footnotes.
          </p>
          <p>
            Rules move quickly, so guides are dated and re-checked rather than written once. Recent
            changes we track include higher salary thresholds and longer settlement timelines in the
            UK, annual permanent-residence targets and study permit caps in Canada, points and
            skills-list revisions in Australia, digital nomad and flat-tax regimes in Italy and
            Portugal, and fee increases across US petitions. Where a rule is genuinely unsettled —
            a bill in progress, a quota not yet published — we say so plainly instead of guessing,
            and we tell you which official page to watch for the confirmed version.
          </p>
          <p>
            What a realistic timeline looks like: most successful moves take six to eighteen months
            from first research to landing. Skilled-worker routes usually begin with an employer
            willing to sponsor, followed by a credential or English-language assessment, then the
            visa application itself, and finally the practical layer — shipping, tenancy deposits,
            school places and a first local bank account. Study routes run to academic calendars, so
            the real deadline is usually the tuition deposit and proof-of-funds date rather than the
            visa. Family and partner routes hinge on documentary evidence gathered over months, not
            weeks. We flag those sequencing traps in each guide because a correct application filed
            in the wrong order still costs you a season.
          </p>
          <p>
            Money is the part people underestimate. Beyond the visa fee itself there are health
            surcharges, biometrics, document translation and legalisation, credential recognition,
            flights, one to three months of rent up front, and a buffer for the weeks before your
            first salary clears. Newcomers also arrive with no local credit history, which can mean
            higher deposits on housing, phone contracts and car finance for the first year. Our
            finance and housing guides put concrete numbers against each of those lines for all six
            countries so you can build a landing budget instead of guessing at one.
          </p>
          <p>
            Finally, a word on what we deliberately do not do. We do not rank countries into a
            single "best" list, because the right destination depends on your occupation, family
            situation, savings and risk tolerance. We do not publish shortcut claims,
            guaranteed-approval promises or agent referrals. And we do not paywall anything: every
            guide, comparison and checklist on the site is free to read without an account. If a
            guide helped you, the most useful thing you can do is tell us where it was wrong or out
            of date so the next reader gets a better version.
          </p>
        </div>
      </section>

      <section className="mt-12 rounded-2xl border border-accent/20 bg-card/60 p-6">
        <h3 className="font-display text-lg font-bold text-foreground">Who it's for</h3>
        <ul className="mt-4 space-y-3 text-sm leading-7 text-foreground/80">
          <li>Skilled workers comparing sponsorship and points-based routes</li>
          <li>Students planning post-study work and permanent residency</li>
          <li>Remote workers and freelancers assessing nomad visas and tax residency</li>
          <li>Families budgeting rent, schooling, healthcare and relocation costs</li>
          <li>New arrivals opening bank accounts and building local credit</li>
        </ul>
        <p className="mt-5 border-t border-border pt-4 text-sm leading-relaxed text-muted-foreground">
          Free to read, no sign-up required, and updated as rules change.
        </p>
      </section>

      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-accent">
          Our mission: one honest guide to moving abroad
        </h2>
        <p className="mt-4 text-sm leading-7 text-foreground/80">
          Moving abroad means navigating complex visa requirements, understanding a new job market,
          managing taxes in two countries at once, opening a bank account without a local credit
          history, and securing housing in a city you may never have visited. LifeWestWay simplifies
          that journey with beginner-friendly, research-backed guides to the six destinations most
          people ask about. Every country hub explains the main visa routes and who qualifies,
          realistic processing times and government fees, the salary thresholds employers must meet,
          and the residency clock that leads to permanent status and citizenship. Alongside the
          immigration detail you will find practical living information: what rent, health cover,
          transport and groceries actually cost each month, how income tax and social contributions
          are calculated, which banks accept newcomers, and how to build credit from zero. We update
          the guidance for 2026 rules, cite official government sources, and write in plain English
          so that students, skilled workers, remote professionals, retirees and families can compare
          their options honestly before spending money on applications or advisers.
        </p>
      </section>

      {/* Independence disclaimer block */}
      <section className="mt-12 rounded-2xl border border-accent/25 bg-card/60 p-5 sm:p-6">
        <h3 className="font-display text-sm font-bold uppercase tracking-[0.18em] text-accent">
          Independence
        </h3>
        <p className="mt-2 text-sm leading-6 text-foreground/75">
          LifeWestWay is an independent publisher and is not affiliated with, endorsed by, or acting
          on behalf of any government agency, immigration authority, or financial institution
          mentioned on this site. We do not sell immigration services, take referral fees, or
          represent any single destination over another.
        </p>
      </section>

      {/* Get in touch */}
      <section className="mt-12 rounded-2xl border border-border bg-navy/30 p-6 sm:p-8">
        <h2 className="font-display text-2xl font-bold text-foreground">Get in Touch</h2>
        <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground/75">
          Have a question, a correction to suggest, or a topic you'd like us to cover? We'd
          love to hear from you. Whether you spotted an outdated fee figure, want to share your own
          relocation experience, or have an idea for a guide we haven't written yet, reach out
          — we read every message.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Link title="Contact the LifeWestWay editorial team"
            to="/contact"
            className="inline-flex items-center rounded-md bg-accent px-5 py-2.5 text-sm font-semibold text-accent-foreground transition-colors hover:bg-accent/90"
          >
            Contact our team →
          </Link>
          <Link title="Read the LifeWestWay editorial process"
            to="/editorial-process"
            className="inline-flex items-center rounded-md border border-border px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:border-accent hover:text-accent"
          >
            How we work
          </Link>
        </div>
      </section>

      <ArticleDisclaimer className="mt-12" />
    </div>
  );
}
